import React, { useState, useRef, useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  MessageSquare,
  BarChart3,
  MousePointerClick,
  Library,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  LogOut,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const ITEMS = [
  {
    to: "/",
    label: "Dashboard",
    icon: LayoutDashboard,
    perm: "analytics:read",
  },
  {
    label: "Conversations",
    icon: MessageSquare,
    perm: "conversations:read",
    children: [
      {
        to: "/conversations",
        label: "Beta Tester",
        perm: "conversations:read",
      },
      // {
      //   to: "/sessions",
      //   label: "Alpha Tester",
      //   perm: "analytics:read",
      // },
    ],
  },
  {
    to: "/analytics",
    label: "Analytics",
    icon: BarChart3,
    perm: "analytics:read",
  },
  {
    to: "/detailed-answer-clicks",
    label: "Answer Detail Clicks",
    icon: MousePointerClick,
    perm: "conversations:read",
  },
  { to: "/kms", label: "KMS Library", icon: Library, perm: "kms:read" },
  { to: "/kms/gaps", label: "KMS Gaps", icon: AlertTriangle, perm: "kms:read" },
];

export default function Sidebar({ onRequestLogout }) {
  const { user, hasPermission } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState({ Conversations: true });
  const [overlay, setOverlay] = useState({ group: null, top: 0, visible: false });
  const groupRefs = useRef({});
  const sidebarRef = useRef(null);

  const visible = ITEMS.filter(
    (item) => !item.perm || hasPermission(item.perm),
  );

  // Auto-collapse sidebar on smaller screens (< 768px)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCollapsed(true);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close floating popover overlay on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (overlay.visible && sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setOverlay({ group: null, top: 0, visible: false });
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [overlay.visible]);

  // Generate initials for avatar (e.g. "MU" for Mukesh)
  const getInitials = (u) => {
    if (u?.firstname && u?.lastname) {
      return `${u.firstname[0]}${u.lastname[0]}`.toUpperCase();
    }
    if (u?.name) {
      const parts = u.name.trim().split(" ");
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return u.name.slice(0, 2).toUpperCase();
    }
    if (u?.email) {
      return u.email.slice(0, 2).toUpperCase();
    }
    return "MU";
  };

  return (
    <aside
      ref={sidebarRef}
      style={{
        width: isCollapsed ? 72 : 260,
        height: "100vh",
        background: "var(--paper-2, #fbf8f5)",
        borderRight: "1px solid var(--ink-200, #eae2d9)",
        padding: isCollapsed ? "16px 10px" : "20px 16px",
        display: "flex",
        flexDirection: "column",
        position: "sticky",
        top: 0,
        boxShadow: "4px 0 24px rgba(0, 0, 0, 0.02)",
        zIndex: 50,
        overflowX: "hidden",
        overflowY: "auto",
        boxSizing: "border-box",
        transition: "width 0.25s cubic-bezier(0.4, 0, 0.2, 1), padding 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Brand & Toggle Header */}
      <div
        style={{
          height: 42,
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "space-between",
          padding: isCollapsed ? 0 : "0 4px",
          transition: "justify-content 0.2s ease",
        }}
      >
        {!isCollapsed && (
          <img
            src="/Dnyankosh-logo2.png"
            alt="Dyanakosh Logo"
            style={{
              maxHeight: "36px",
              maxWidth: "160px",
              objectFit: "contain",
              transition: "opacity 0.2s ease",
            }}
          />
        )}
        <button
          onClick={() => {
            setIsCollapsed(!isCollapsed);
            setOverlay({ group: null, top: 0, visible: false });
          }}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            padding: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--ink-500, #8c7a6b)",
            borderRadius: "6px",
            transition: "background 0.15s ease, color 0.15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--paper-3, #f0e8df)";
            e.currentTarget.style.color = "var(--ink-800, #2c1a0e)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "var(--ink-500, #8c7a6b)";
          }}
          title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </button>
      </div>

      {/* Divider */}
      <div
        style={{
          height: 1,
          background:
            "linear-gradient(90deg, transparent, var(--ink-200, #e8dfd5), transparent)",
          marginBottom: 16,
          opacity: 0.6,
          flexShrink: 0,
        }}
      />

      {/* Navigation items */}
      <nav
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "6px",
          flex: 1,
        }}
      >
        {visible.map((item) => {
          if (item.children) {
            const isOpen = !!openGroups[item.label];
            return (
              <div
                key={item.label}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  position: "relative",
                }}
              >
                {/* Group Parent Trigger */}
                <div
                  ref={(el) => (groupRefs.current[item.label] = el)}
                  role="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isCollapsed) {
                      const el = groupRefs.current[item.label];
                      if (el) {
                        const rect = el.getBoundingClientRect();
                        setOverlay((prev) => ({
                          group: item.label,
                          top: rect.top,
                          visible: !(prev.group === item.label && prev.visible),
                        }));
                      }
                    } else {
                      setOpenGroups((prev) => ({
                        ...prev,
                        [item.label]: !prev[item.label],
                      }));
                    }
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isCollapsed ? "center" : "flex-start",
                    padding: isCollapsed ? "10px 0" : "10px 12px",
                    borderRadius: "10px",
                    color: "var(--ink-700, #4a382a)",
                    fontWeight: 600,
                    fontSize: "14px",
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "background 0.15s ease, color 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "var(--paper-3, #f4ece4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                  title={isCollapsed ? item.label : undefined}
                >
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <item.icon
                      size={18}
                      strokeWidth={2}
                      style={{ color: "var(--ink-500, #8c7a6b)" }}
                    />
                  </div>

                  {!isCollapsed && (
                    <>
                      <span
                        style={{
                          marginLeft: "12px",
                          letterSpacing: "0.2px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          flex: 1,
                        }}
                      >
                        {item.label}
                      </span>
                      {/* Dropdown chevron indicator */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "var(--ink-500, #8c7a6b)",
                        }}
                      >
                        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </div>
                    </>
                  )}
                </div>

                {/* Inline sub-items when expanded */}
                {!isCollapsed && isOpen && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                      paddingLeft: "32px",
                    }}
                  >
                    {item.children
                      .filter((child) => !child.perm || hasPermission(child.perm))
                      .map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          end={child.to === "/conversations"}
                          className={({ isActive }) =>
                            `nav-item ${isActive ? "is-active" : ""}`
                          }
                          style={({ isActive }) => ({
                            display: "flex",
                            alignItems: "center",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            color: isActive
                              ? "var(--saffron-deep, #c86d27)"
                              : "var(--ink-700, #4a382a)",
                            background: isActive
                              ? "var(--saffron-tint, #fdf6ee)"
                              : "transparent",
                            fontWeight: isActive ? 600 : 500,
                            fontSize: "13px",
                            textDecoration: "none",
                            position: "relative",
                            transition: "all 0.15s ease",
                          })}
                        >
                          {({ isActive }) => (
                            <>
                              {isActive && (
                                <div
                                  style={{
                                    position: "absolute",
                                    left: 0,
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    width: "3px",
                                    height: "16px",
                                    background: "var(--saffron, #e08e45)",
                                    borderRadius: "0 4px 4px 0",
                                  }}
                                />
                              )}
                              <span
                                style={{
                                  letterSpacing: "0.2px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {child.label}
                              </span>
                            </>
                          )}
                        </NavLink>
                      ))}
                  </div>
                )}

                {/* Floating overlay popover for sub-items in collapsed mode */}
                {isCollapsed && overlay.visible && overlay.group === item.label && (
                  <div
                    style={{
                      position: "fixed",
                      left: 78,
                      top: overlay.top,
                      background: "#ffffff",
                      border: "1px solid var(--ink-200, #e0d5c8)",
                      borderRadius: 10,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      zIndex: 100,
                      minWidth: 170,
                      padding: "6px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                      animation: "fadeIn 0.15s ease-out",
                    }}
                  >
                    {item.children
                      .filter((child) => !child.perm || hasPermission(child.perm))
                      .map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          end={child.to === "/conversations"}
                          onClick={() =>
                            setOverlay({ group: null, top: 0, visible: false })
                          }
                          style={({ isActive }) => ({
                            display: "block",
                            padding: "8px 12px",
                            borderRadius: "6px",
                            color: isActive
                              ? "var(--saffron-deep, #c86d27)"
                              : "var(--ink-700, #4a382a)",
                            background: isActive
                              ? "var(--saffron-tint, #fdf6ee)"
                              : "transparent",
                            textDecoration: "none",
                            fontWeight: isActive ? 600 : 500,
                            fontSize: 13,
                            transition: "background 0.15s ease",
                          })}
                        >
                          {child.label}
                        </NavLink>
                      ))}
                  </div>
                )}
              </div>
            );
          }

          const { to, label, icon: Icon, highlight } = item;
          return (
            <NavLink
              key={to}
              to={to}
              end={to === "/" || to === "/kms"}
              className={({ isActive }) =>
                `nav-item ${isActive ? "is-active" : ""}`
              }
              style={({ isActive }) => ({
                display: "flex",
                alignItems: "center",
                justifyContent: isCollapsed ? "center" : "flex-start",
                padding: isCollapsed ? "10px 0" : "10px 12px",
                borderRadius: "10px",
                color: isActive ? "var(--saffron-deep, #c86d27)" : "var(--ink-700, #4a382a)",
                background: isActive ? "var(--saffron-tint, #fdf6ee)" : "transparent",
                fontWeight: isActive ? 600 : 500,
                fontSize: "14px",
                textDecoration: "none",
                position: "relative",
                transition: "all 0.15s ease",
              })}
              title={isCollapsed ? label : undefined}
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: "3px",
                        height: "18px",
                        background: "var(--saffron, #e08e45)",
                        borderRadius: "0 4px 4px 0",
                      }}
                    />
                  )}

                  <div
                    style={{
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Icon
                      size={18}
                      strokeWidth={isActive ? 2.5 : 2}
                      style={{
                        color: isActive
                          ? "var(--saffron-deep, #c86d27)"
                          : "var(--ink-500, #8c7a6b)",
                      }}
                    />
                  </div>

                  {!isCollapsed && (
                    <>
                      <span
                        style={{
                          marginLeft: "12px",
                          letterSpacing: "0.2px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {label}
                      </span>

                      {highlight && (
                        <span
                          style={{
                            marginLeft: "auto",
                            fontSize: "10px",
                            fontWeight: 700,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            color: "var(--saffron-deep, #c86d27)",
                            background: "var(--saffron-light, #fbe8d5)",
                            padding: "2px 6px",
                            borderRadius: "12px",
                          }}
                        >
                          AI
                        </span>
                      )}
                    </>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* User / Profile Section */}
      <div
        style={{
          marginTop: "auto",
          paddingTop: 16,
          borderTop: "1px solid var(--ink-200, #e8dfd5)",
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: isCollapsed ? "center" : "stretch",
            background: isCollapsed ? "transparent" : "var(--paper-3, #f7f2ec)",
            border: isCollapsed ? "none" : "1px solid var(--ink-200, #eae2d9)",
            borderRadius: "14px",
            padding: isCollapsed ? "4px 0" : "12px",
            gap: 12,
            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          {/* User Avatar + Info */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: isCollapsed ? "center" : "flex-start",
              gap: 10,
              minWidth: 0,
            }}
          >
            {/* Avatar Circle with Ring */}
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #e08e45 0%, #c86d27 100%)",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.5px",
                boxShadow: "0 2px 6px rgba(200, 109, 39, 0.25)",
                position: "relative",
              }}
            >
              {getInitials(user)}
              {/* Online Indicator Dot */}
              <span
                style={{
                  position: "absolute",
                  bottom: 1,
                  right: 1,
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#22c55e",
                  border: "1.5px solid #ffffff",
                }}
              />
            </div>

            {/* Name & Role (Hidden when collapsed) */}
            {!isCollapsed && (
              <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#2c1a0e",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1.2,
                  }}
                >
                  {user?.firstname || user?.name || "Mukesh"}
                </span>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#a0816c",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    marginTop: 2,
                  }}
                >
                  {user?.role || "USER"}
                </span>
              </div>
            )}
          </div>

          {/* Sign Out Action Button */}
          {!isCollapsed ? (
            <button
              onClick={() => onRequestLogout?.()}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                width: "100%",
                background: "var(--paper-1, #ffffff)",
                border: "1px solid var(--ink-200, #e0d5c8)",
                borderRadius: "8px",
                padding: "8px 12px",
                color: "#5c3d28",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.15s ease",
                boxShadow: "0 1px 2px rgba(0,0,0,0.03)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#fef2f2";
                e.currentTarget.style.borderColor = "#fca5a5";
                e.currentTarget.style.color = "#dc2626";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "var(--paper-1, #ffffff)";
                e.currentTarget.style.borderColor = "var(--ink-200, #e0d5c8)";
                e.currentTarget.style.color = "#5c3d28";
              }}
            >
              <LogOut size={13} />
              <span>Sign out</span>
            </button>
          ) : (
            <button
              onClick={() => onRequestLogout?.()}
              style={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                border: "1px solid var(--ink-200, #e0d5c8)",
                background: "var(--paper-3, #f7f2ec)",
                color: "#5c3d28",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#fef2f2";
                e.currentTarget.style.borderColor = "#fca5a5";
                e.currentTarget.style.color = "#dc2626";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "var(--paper-3, #f7f2ec)";
                e.currentTarget.style.borderColor = "var(--ink-200, #e0d5c8)";
                e.currentTarget.style.color = "#5c3d28";
              }}
              title="Sign out"
            >
              <LogOut size={16} />
            </button>
          )}
        </div>
      </div>
    </aside>
  );
}